import moment from 'moment';
import {
  hasVerboxOwner,
  hiddenVerboxClassName,
  isVerboxEnabled,
  loadVerboxWidget,
  prepareVerboxOwner
} from './VerboxChat';

export type MaintenanceDevice = {
  id?: unknown;
  model?: unknown;
  description?: unknown;
  serialNumber?: unknown;
  clockSystemSerialNumber?: unknown;
  nextMaintenanceDate?: unknown;
  users?: Array<{
    id?: unknown;
    login?: unknown;
    email?: unknown;
    phone?: unknown;
  }> | null;
};

export function getMaintenanceDate(value: unknown) {
  return moment(typeof value === 'string' ? value : '', ['YYYY-MM-DD', moment.ISO_8601], true);
}

export function needsMaintenance(value: unknown) {
  const date = getMaintenanceDate(value);
  return date.isValid() && date.startOf('day').isSameOrBefore(moment().startOf('day'));
}

const text = (value: unknown) => typeof value === 'string' || typeof value === 'number'
  ? String(value).trim() : '';

const getCurrentIdentity = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return {
      id: text(user?.id).toLocaleLowerCase(),
      login: text(user?.login || user?.name).toLocaleLowerCase(),
      email: text(user?.email).toLocaleLowerCase()
    };
  } catch {
    return {id: '', login: '', email: ''};
  }
};

const getCurrentDeviceUser = (device: MaintenanceDevice) => {
  const users = device.users || [];
  const current = getCurrentIdentity();
  const matches = (value: unknown) => {
    const normalized = text(value).toLocaleLowerCase();
    return Boolean(normalized) && [current.id, current.login, current.email].includes(normalized);
  };

  const matchedUser = users.find(user =>
    matches(user.id) || matches(user.login) || matches(user.email)
  );

  // A single linked user is unambiguous even when the authentication response
  // contains only a display name instead of the user's id/login.
  return matchedUser || (users.length === 1 ? users[0] : undefined);
};

export async function openMaintenanceChat(device: MaintenanceDevice, cardUrl: string) {
  if (!isVerboxEnabled || !hasVerboxOwner()) throw new Error('Чат доступен после входа в приложение.');
  await loadVerboxWidget();
  await prepareVerboxOwner();

  const api = window.Verbox;
  if (!api) throw new Error('Чат пока недоступен. Попробуйте ещё раз.');

  const date = getMaintenanceDate(device.nextMaintenanceDate);
  const deviceName = text(device.description) || text(device.model) || 'Не указано';
  const maintenanceDate = date.isValid() ? date.format('DD.MM.YYYY') : 'Не указана';
  const currentUser = getCurrentDeviceUser(device);
  const login = text(currentUser?.login);
  const email = text(currentUser?.email) || (login.includes('@') ? login : '');
  const phone = text(currentUser?.phone);
  const description = [
    'Требуется техническое обслуживание',
    `Устройство: ${deviceName}`,
    `Модель: ${text(device.model) || 'Не указана'}`,
    `ID устройства: ${text(device.id) || 'Не указан'}`,
    `Серийный номер: ${text(device.serialNumber) || 'Не указан'}`,
    `Номер часовой системы: ${text(device.clockSystemSerialNumber) || 'Не указан'}`,
    `Дата ТО: ${maintenanceDate}`,
    `Карточка: ${cardUrl}`,
    `Логин пользователя: ${login || 'Не найден'}`,
    `Email пользователя: ${email || 'Не найден'}`,
    `Телефон пользователя: ${phone || 'Не найден'}`
  ].join('\n');

  // Send only the explicit support context, never the full device or auth object.
  // Keep it until the next explicit request, not merely until navigation.
  api('setClientInfo', {
    ...(login && {firstName: login}),
    ...(email && {email}),
    ...(phone && {phone}),
    isUpdateMultiContacts: true,
    custom: {
      'Последнее обращение по ТО': 'Требуется техническое обслуживание',
      'Устройство': deviceName,
      'Модель': text(device.model) || 'Не указана',
      'ID устройства': text(device.id),
      'Серийный номер': text(device.serialNumber) || 'Не указан',
      'Номер часовой системы': text(device.clockSystemSerialNumber) || 'Не указан',
      'Дата ТО': maintenanceDate,
      'Карточка устройства': cardUrl,
      'Логин пользователя': login || 'Не найден',
      'Email пользователя': email || 'Не найден',
      'Телефон пользователя': phone || 'Не найден',
      'Описание': description
    }
  }, (error?: Error) => {
    if (error) console.error('Не удалось передать данные пользователя в Verbox:', error);
  });
  document.body.classList.remove(hiddenVerboxClassName);
  api('openSupport');
}
